import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown } from "react-bootstrap";
import { ThreeDot } from "react-loading-indicators";
import swal from "sweetalert";
import NoData from "../../../assets/img/no-data.png";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import { format } from "date-fns";
import NoAccess from "../../../assets/img/NoAccess.png";
import BASE_URL from "../../../assets/global/url";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../hooks/customHook/paginationHook/usePagination";
import { getRetainedNetIncomes } from "../../../services/RetainedNetIncome/getRetainedNetIncomes.service";
import { generateTransactionNumber } from "../../../services/general/generateTransactionNumber.service";
import { createProfitDistribution } from "../../../services/ProfitDistribution/createProfitDistribution.service";
import { getProfitDistributions } from "../../../services/ProfitDistribution/getProfitDistributions.service";
import { deleteProfitDistribution } from "../../../services/ProfitDistribution/deleteProfitDistribution.service";
import { formatToPHP } from "../../../utils/numberFormatter";
import {
  ACCENT,
  ACCENT_SOFT,
  MUTED,
  ActionsToggle,
  CardLabel,
  PeriodStrip,
  cardStyle,
} from "../components/Cutoff/CardParts";
import AddProfitDistributionModal, {
  getAmountToDistributeError,
} from "./component/AddProfitDistributionModal";
import {
  StatusPill,
  confirmApproval,
  getApprovalBlockReason,
  canOpenFundAllocation,
  isAllocationApproved,
} from "./component/ProfitDistributionStatus";
import { toRetainedNetIncomeOption } from "./component/retainedNetIncomeOption";

const INITIAL_FORM = {
  selectedRetainedNetIncomes: [],
  transactionNumber: "",
  distributionName: "",
  distributionDate: "",
  // Net income of the selected Retained Net Incomes (null while loading) and
  // the part of it to distribute (null = the whole net income)
  netIncomeAvailable: 0,
  amountToDistribute: null,
  remarks: "",
};

const formatDate = (date) =>
  date ? format(new Date(date), "MMM dd, yyyy") : "--";

const getCreatorName = (distribution) => {
  const creator = distribution.profit_distribution_created_masterlist;
  return creator ? `${creator.fname} ${creator.lname}` : "--";
};

const getInitials = (distribution) => {
  const creator = distribution.profit_distribution_created_masterlist;
  if (!creator) return "?";
  return `${creator.fname?.[0] || ""}${creator.lname?.[0] || ""}`.toUpperCase();
};

const STATUS_FILTERS = [
  "All",
  "Pending",
  "Approved",
  "Rejected",
  "Partially-Distributed",
  "Distributed",
  "Approved Allocated Funds",
  "Rejected Allocated Funds",
];

// Clicking a card opens its view page; Fund Allocation (Approved only) and
// the other actions are in the three-dot menu
function ProfitDistributionCard({
  distribution,
  onView,
  onFundAllocation,
  onDelete,
  onApprove,
  onReject,
}) {
  const records = distribution.retained_net_incomes || [];
  // Approve/Reject stay in the menu but disabled, with the reason below them
  const approvalBlockReason = getApprovalBlockReason(distribution);

  return (
    <div
      className="card border-0 rounded-4 h-100 hover-lift transition-shadow"
      style={cardStyle()}
      onClick={onView}>
      <div className="card-body p-4 d-flex flex-column gap-3">
        {/* Header: transaction no., name, status, actions */}
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ minWidth: 0 }}>
            <div
              className="text-uppercase fw-medium mb-1"
              style={{
                fontSize: "0.7rem",
                letterSpacing: "0.05em",
                color: MUTED,
              }}>
              {distribution.transaction_number}
            </div>
            <div className="fw-semibold text-dark fs-5 text-truncate">
              {distribution.name}
            </div>
          </div>
          <div className="d-flex align-items-center gap-1 flex-shrink-0">
            <StatusPill status={distribution.status} />
            <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
              <Dropdown.Toggle
                as={ActionsToggle}
                id={`pd-actions-${distribution.id}`}
              />
              <Dropdown.Menu className="shadow-sm border-0 rounded-3">
                <Dropdown.Item
                  onClick={onView}
                  className="d-flex align-items-center px-3 py-2">
                  <i className="bx bx-show me-2 fs-5 text-success"></i>
                  <span className="text-body-secondary">View</span>
                </Dropdown.Item>
                {onFundAllocation && (
                  <Dropdown.Item
                    onClick={onFundAllocation}
                    className="d-flex align-items-center px-3 py-2">
                    <i className="bx bx-wallet me-2 fs-5 text-primary"></i>
                    <span className="text-body-secondary">Fund Allocation</span>
                  </Dropdown.Item>
                )}
                {onApprove && (
                  <Dropdown.Item
                    onClick={onApprove}
                    disabled={Boolean(approvalBlockReason)}
                    className="d-flex align-items-center px-3 py-2">
                    <i
                      className={`bx bx-check-circle me-2 fs-5 ${
                        approvalBlockReason ? "" : "text-success"
                      }`}></i>
                    <span className="text-body-secondary">Approve</span>
                  </Dropdown.Item>
                )}
                {onReject && (
                  <Dropdown.Item
                    onClick={onReject}
                    disabled={Boolean(approvalBlockReason)}
                    className="d-flex align-items-center px-3 py-2">
                    <i
                      className={`bx bx-x-circle me-2 fs-5 ${
                        approvalBlockReason ? "" : "text-danger"
                      }`}></i>
                    <span className="text-body-secondary">Reject</span>
                  </Dropdown.Item>
                )}
                {(onApprove || onReject) && approvalBlockReason && (
                  <Dropdown.ItemText
                    className="d-flex align-items-start gap-1 px-3 pb-2 pt-0 small text-muted"
                    style={{ maxWidth: 280, whiteSpace: "normal" }}>
                    <i className="bx bx-info-circle mt-1 flex-shrink-0"></i>
                    <span>{approvalBlockReason}</span>
                  </Dropdown.ItemText>
                )}
                {onDelete && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={onDelete}
                      className="d-flex align-items-center px-3 py-2 text-danger">
                      <i className="bx bx-trash me-2 fs-5"></i>
                      <span>Delete</span>
                    </Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Total net income */}
        <div>
          <CardLabel className="mb-1">Total Net Income</CardLabel>
          <div
            className="fs-3 fw-bold lh-1"
            style={{
              color: distribution.net_income < 0 ? "#d92d20" : "#079455",
            }}>
            {formatToPHP(distribution.net_income || 0)}
          </div>
          <div className="small mt-2" style={{ color: MUTED }}>
            To distribute:{" "}
            <span className="fw-semibold text-dark">
              {formatToPHP(distribution.amount_to_distribute || 0)}
            </span>
          </div>
        </div>

        {/* Period strip */}
        <PeriodStrip from={distribution.from} to={distribution.to} />

        {/* Included Retained Net Incomes */}
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="small fw-medium me-1" style={{ color: MUTED }}>
            {records.length} retained net income
            {records.length === 1 ? "" : "s"}
          </span>
          {records.map((record) => (
            <span
              key={record.id}
              className="rounded-pill px-2 py-1 small fw-medium"
              style={{ backgroundColor: ACCENT_SOFT, color: ACCENT }}
              title={record.transaction_number}>
              {record.name}
            </span>
          ))}
        </div>

        {/* Footer: creator and distribution date */}
        <div
          className="mt-auto pt-3 d-flex align-items-center justify-content-between small"
          style={{ borderTop: "1px solid #f0f1f5" }}>
          <div
            className="d-flex align-items-center gap-2"
            style={{ minWidth: 0 }}>
            <span
              className="rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold flex-shrink-0"
              style={{
                width: 26,
                height: 26,
                fontSize: "0.7rem",
                backgroundColor: ACCENT_SOFT,
                color: ACCENT,
              }}>
              {getInitials(distribution)}
            </span>
            <span className="text-truncate text-body-secondary">
              {getCreatorName(distribution)}
            </span>
          </div>
          <span className="text-nowrap" style={{ color: MUTED }}>
            Distributed {formatDate(distribution.distribution_date)}
          </span>
        </div>
      </div>
    </div>
  );
}

function NewReturnEarnings({ authrztn }) {
  const userLoggedID = useDecodeToken();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  // Profit Distribution table (server-side paginated)
  const pagination = useServerPagination(
    `${BASE_URL}/profit_distribution/getProfitDistributionsPaginated`,
    10,
  );

  const handleFundAllocation = (distribution) => {
    navigate(
      `/accounting/profit-distribution/fund-allocation/${distribution.id}`,
    );
  };

  // Status filter for the cards
  const [statusFilter, setStatusFilter] = useState("All");

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    pagination.updateParams({ status: status === "All" ? "" : status });
  };

  // Every active distribution, used to hide Retained Net Incomes that are
  // already taken and to show only the status filters that have data
  const [distributions, setDistributions] = useState([]);

  // Add Profit Distribution modal
  const [showAdd, setShowAdd] = useState(false);
  const [validated, setValidated] = useState(false);
  const [retainedNetIncomeData, setRetainedNetIncomeData] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);

  // isSaving drives the UI; the ref blocks a second submit immediately,
  // before the state update has re-rendered.
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  // Update a single field of the form
  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const fetchRetainedNetIncomes = async () => {
    setIsLoading(true);
    try {
      const data = await getRetainedNetIncomes();
      setRetainedNetIncomeData(data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDistributions = async () => {
    try {
      const data = await getProfitDistributions();
      setDistributions(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchRetainedNetIncomes();
    fetchDistributions();
  }, []);

  // Status chips only for statuses that at least one distribution has;
  // "All" is always shown
  const availableStatuses = new Set(distributions.map((item) => item.status));
  const visibleStatusFilters = STATUS_FILTERS.filter(
    (status) => status === "All" || availableStatuses.has(status),
  );

  // Back to "All" when the selected status has no data any more (e.g. its
  // last distribution was deleted or moved to another status)
  useEffect(() => {
    if (statusFilter !== "All" && !availableStatuses.has(statusFilter)) {
      handleStatusFilter("All");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributions]);

  // Retained Net Incomes already used by an existing Profit Distribution
  // can't be reused; a rejected one no longer holds its records
  const usedRetainedNetIncomeIds = new Set(
    distributions
      .filter((item) => item.status !== "Rejected")
      .flatMap((item) =>
        item.profit_distribution_retained_net_incomes.map(
          (link) => link.retained_net_income_id,
        ),
      ),
  );

  const retainedNetIncomeOptions = retainedNetIncomeData
    .filter((record) => !usedRetainedNetIncomeIds.has(record.id))
    .map(toRetainedNetIncomeOption);

  const resetForm = () => {
    setValidated(false);
    setForm(INITIAL_FORM);
  };

  const handleShowAdd = async () => {
    setValidated(false);
    setForm({ ...INITIAL_FORM, distributionDate: new Date() });
    setShowAdd(true);

    try {
      const code = await generateTransactionNumber("PD");
      handleFieldChange("transactionNumber", code);
    } catch (error) {
      console.log(error);
      swal({
        icon: "error",
        title: "Something went wrong",
        text: "Unable to generate a transaction number.",
      });
    }
  };

  const handleClose = () => {
    if (isSavingRef.current) return;
    setShowAdd(false);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSavingRef.current) return;
    setValidated(true);

    if (
      !form.transactionNumber ||
      form.selectedRetainedNetIncomes.length === 0 ||
      !form.distributionName.trim() ||
      !form.distributionDate
    ) {
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
      return;
    }

    const amountError = getAmountToDistributeError(form);
    if (amountError) {
      swal({ icon: "error", title: "Amount to Distribute", text: amountError });
      return;
    }

    swal({
      title: "Confirm Action",
      text: "Are you sure you want to add this Profit Distribution?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed || isSavingRef.current) return;
      isSavingRef.current = true;
      setIsSaving(true);

      const payload = {
        retained_net_income_ids: form.selectedRetainedNetIncomes.map(
          (option) => option.value,
        ),
        transaction_number: form.transactionNumber,
        name: form.distributionName.trim(),
        distribution_date: format(
          new Date(form.distributionDate),
          "yyyy-MM-dd",
        ),
        amount_to_distribute: form.amountToDistribute,
        remarks: form.remarks.trim(),
        userLoggedID,
      };

      let saved = false;
      try {
        await createProfitDistribution(payload);
        saved = true;
      } catch (error) {
        console.log(error);
        swal({
          icon: "error",
          title: "Something went wrong",
          text:
            error.response?.data?.message ||
            "Unable to save the Profit Distribution.",
        });
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }

      if (saved) {
        swal({
          icon: "success",
          title: "Profit Distribution added",
          text: "The Profit Distribution has been saved.",
        });
        handleClose();
        fetchDistributions();
        pagination.refreshData();
      }
    });
  };

  const canApprove = (distribution) =>
    authrztn.includes("Retained-Approve") && distribution.status === "Pending";

  const handleApproval = async (distribution, action) => {
    const changed = await confirmApproval(distribution, action, userLoggedID);
    if (!changed) return;

    // A rejected distribution frees its Retained Net Incomes
    fetchDistributions();
    pagination.refreshData();
  };

  const handleDelete = (distribution) => {
    // An approved fund allocation has payments: paid ones are returned to
    // their source accounts, unpaid cheques / bank transactions are removed
    const paymentsNote = isAllocationApproved(distribution)
      ? "\n\nIts fund allocation payments will be cancelled: payments already paid (Cash, paid cheques, confirmed bank transactions) go back to their source accounts, and unpaid cheques and bank transactions are removed."
      : "";

    swal({
      title: "Confirm Action",
      text: `Are you sure you want to delete "${distribution.name}"? Its Retained Net Incomes will be available again for another Profit Distribution.${paymentsNote}`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;

      let result;
      try {
        result = await deleteProfitDistribution({
          id: distribution.id,
          userLoggedID,
        });
      } catch (error) {
        console.log(error);
        swal({
          icon: "error",
          title: error.response?.data?.title || "Something went wrong",
          text:
            error.response?.data?.message ||
            "Unable to delete the Profit Distribution.",
        });
        return;
      }

      swal({
        icon: "success",
        title: "Profit Distribution deleted",
        text: result?.message || `"${distribution.name}" has been deleted.`,
        timer: 2000,
        button: false,
      });
      // Freed Retained Net Incomes become selectable again
      fetchDistributions();
      // Step back a page when the last card on it was deleted
      if (pagination.data.length === 1 && pagination.currentPage > 1) {
        pagination.setCurrentPage(pagination.currentPage - 1);
      } else {
        pagination.refreshData();
      }
    });
  };

  return authrztn.includes("Retained-View") ? (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3 text-uppercase">Profit Distribution</span>
        </div>
        <div className="d-flex gap-2">
          {authrztn.includes("Retained-Add") && (
            <Button
              className="btn d-flex align-items-center title-button"
              variant="primary"
              onClick={handleShowAdd}>
              <i className="bx bx-plus fs-5"></i> Add Profit Distribution
            </Button>
          )}
        </div>
      </div>

      <div className="w-100 pt-2 px-4 d-flex flex-wrap align-items-center gap-2">
        <span className="small fw-medium me-1" style={{ color: MUTED }}>
          Status
        </span>
        {visibleStatusFilters.map((status) => {
          const isActive = statusFilter === status;
          return (
            <button
              key={status}
              type="button"
              className={`pd-status-filter btn btn-sm rounded-pill px-3 fw-medium ${
                isActive ? "is-active" : ""
              }`}
              style={
                isActive
                  ? {
                      backgroundColor: ACCENT,
                      color: "#fff",
                      border: `1px solid ${ACCENT}`,
                    }
                  : {
                      backgroundColor: "transparent",
                      color: ACCENT,
                      border: `1px solid ${ACCENT_SOFT}`,
                    }
              }
              onClick={() => handleStatusFilter(status)}
              aria-pressed={isActive}>
              {status}
            </button>
          );
        })}
      </div>

      <div className="w-100 p-4">
        {pagination.loading ? (
          <div className="p-4 d-flex justify-content-center">
            <ThreeDot color="#0d6efd" size="medium" text="" textColor="" />
          </div>
        ) : pagination.data.length === 0 ? (
          <div className="p-4 d-flex flex-column align-items-center">
            <img src={NoData} alt="No Data" style={{ width: "150px" }} />
            <span className="text-muted mt-2">
              {statusFilter === "All"
                ? "No Profit Distribution yet."
                : `No ${statusFilter} Profit Distribution.`}
            </span>
          </div>
        ) : (
          <div className="row g-4">
            {pagination.data.map((distribution) => (
              <div className="col-md-6" key={distribution.id}>
                <ProfitDistributionCard
                  distribution={distribution}
                  onView={() =>
                    navigate(
                      `/accounting/profit-distribution/${distribution.id}`,
                    )
                  }
                  onFundAllocation={
                    authrztn.includes("Retained-Add") &&
                    canOpenFundAllocation(distribution)
                      ? () => handleFundAllocation(distribution)
                      : null
                  }
                  onDelete={
                    authrztn.includes("Retained-Delete")
                      ? () => handleDelete(distribution)
                      : null
                  }
                  onApprove={
                    canApprove(distribution)
                      ? () => handleApproval(distribution, "approve")
                      : null
                  }
                  onReject={
                    canApprove(distribution)
                      ? () => handleApproval(distribution, "reject")
                      : null
                  }
                />
              </div>
            ))}
          </div>
        )}
        <PaginationControls {...pagination} />
      </div>

      <AddProfitDistributionModal
        show={showAdd}
        onClose={handleClose}
        onSubmit={handleSubmit}
        form={form}
        setForm={setForm}
        onFieldChange={handleFieldChange}
        validated={validated}
        retainedNetIncomeOptions={retainedNetIncomeOptions}
        isLoading={isLoading}
        isSaving={isSaving}
      />
    </div>
  ) : (
    <div className="no-access">
      <img src={NoAccess} alt="NoAccess" className="no-access-img" />
      <h3>You don't have access to this function.</h3>
    </div>
  );
}

export default NewReturnEarnings;
